import { t } from '@lingui/macro'
import {
    EmissionFactorWithGasEmissions,
    GasEmissions,
    TransactionDocumentEstimateActivityLineItem,
} from '@lune-climate/lune'
import { Text, TooltipList } from '@lune-fe/lune-ui-lib'
import { Box } from '@mui/material'
import { FC, useMemo } from 'react'

import { useLuneI18nLocale } from '../../hooks'

import EmissionFactorExplanationCard from './EmissionFactorExplanationCard'
import ExplanationChip from './ExplanationChip'
import ExplanationSteps, { ExplanationStep, ExplanationText } from './ExplanationSteps'

interface TransactionDocumentEmissionsExplanationLineItemSectionProps {
    lineItem: TransactionDocumentEstimateActivityLineItem
    enableEmissionFactorLink: boolean
}

export const TransactionDocumentEmissionsExplanationActivityLineItemSection: FC<
    TransactionDocumentEmissionsExplanationLineItemSectionProps
> = ({ lineItem, enableEmissionFactorLink }) => {
    const locale = useLuneI18nLocale()

    const { item, activity, semanticMatchingModel, emissionFactor: maybeEmissionFactor } = lineItem

    // Cannot be null. Only very old estimate types can have a null emission factor
    const emissionFactor = maybeEmissionFactor!

    const itemStep = useMemo((): JSX.Element | undefined => {
        if (!item) {
            return undefined
        }
        return (
            <>
                <ExplanationText text={t`Extracted the`} />
                <ExplanationChip text={item} />
                <ExplanationText text={t`line item based on your input`} />
            </>
        )
    }, [item, locale])

    const categoryStep = useMemo((): JSX.Element | undefined => {
        // For now the only type of activity transaction we infer is accommodation expenses
        const activityCategory = t`Accommodation`
        return (
            <>
                <ExplanationText text={t`Extracted the`} />
                <ExplanationChip text={activityCategory} />
                <ExplanationText text={t`category based on your input`} />
            </>
        )
    }, [item, locale])

    const genericTextExtractionInfo = useMemo((): JSX.Element => {
        return (
            <>
                <Text variant={'caption'} component={'p'} sx={{ color: 'white' }}>
                    {t`Extracted using AI:`}
                </Text>
                <TooltipList items={[t`Model: Llama 3.1:8b`]} />
            </>
        )
    }, [locale])

    const emissionFactorStep = useMemo((): JSX.Element => {
        return (
            <>
                <ExplanationText text={t`Selected the`} />
                <ExplanationChip text={emissionFactor.name} />
                <ExplanationText text={t`Emission Factor`} />
            </>
        )
    }, [emissionFactor, locale])

    const emissionFactorInfo = useMemo((): JSX.Element => {
        return (
            <>
                <Text variant={'caption'} component={'p'} sx={{ color: 'white' }}>
                    {t`Based on line item description and category:`}
                </Text>
                <TooltipList items={[t`Model: ${semanticMatchingModel}`]} />
            </>
        )
    }, [semanticMatchingModel, locale])

    const activityStep = useMemo((): JSX.Element | undefined => {
        return (
            <>
                <ExplanationText text={t`Extracted`} />
                <ExplanationChip text={activity.value} />
                <ExplanationText text={activity.unit} />
            </>
        )
    }, [activity, locale])

    const formulaExplanation = (
        <ExplanationText text={t`Multiplied the Emission Factor by the inferred activity`} />
    )

    const steps: ExplanationStep[] = [
        ...(itemStep ? [{ description: itemStep, info: genericTextExtractionInfo }] : []),
        ...(categoryStep ? [{ description: categoryStep, info: genericTextExtractionInfo }] : []),
        { description: emissionFactorStep, info: emissionFactorInfo },
        { description: activityStep, info: genericTextExtractionInfo },
        { description: formulaExplanation },
    ]

    return (
        <>
            <Box>
                <Text variant={'h5'} sx={{ mb: 4 }}>
                    {t`Methodology`}
                </Text>
                <ExplanationSteps steps={steps} />
            </Box>
            <Box>
                <EmissionFactorExplanationCard
                    emissionFactor={
                        emissionFactor as Omit<EmissionFactorWithGasEmissions, 'gasEmissions'> & {
                            gasEmissions: GasEmissions
                        }
                    }
                    amount={null}
                    label={emissionFactor.name}
                    {...(enableEmissionFactorLink
                        ? {
                              href: `/emission-factors/${emissionFactor.id}`,
                          }
                        : {})}
                />
            </Box>
        </>
    )
}
